/**
 * Route helpers for the Planning phone frame: titles, back links, top-level
 * routes. The frame renders the main Planning pages (roadmap M-7).
 */
import { ENGINE_BASE } from './mobileEngineRoutes';

export const PLANNING_BASE = '/planning';
export const PLANS_BASE = '/planning/plans';

const PLAN_CYCLE_RE = /^\/planning\/plans\/([^/]+)(?:\/(.*))?$/;

export function planCycleIdFromPath(pathname: string): string | null {
  const m = pathname.match(PLAN_CYCLE_RE);
  return m?.[1] ?? null;
}

export function planSubRouteFromPath(pathname: string): string | null {
  const m = pathname.match(PLAN_CYCLE_RE);
  return m?.[2] ?? null;
}

export function planBaseHref(cycleId: string): string {
  return `${PLANS_BASE}/${cycleId}`;
}

const PLAN_SUB_TITLES: Record<string, string> = {
  milestones: 'Milestones',
  'work-graph': 'Work Graph',
  capacity: 'Capacity',
  governance: 'Governance',
  'assumptions-risks': 'Assumptions & Risks',
  changes: 'Changes',
};

const ROUTE_TITLES: Record<string, string> = {
  [PLANNING_BASE]: 'Planning',
  [`${PLANNING_BASE}/dashboard`]: 'Planning',
  [`${PLANS_BASE}`]: 'Plans',
  [`${PLANNING_BASE}/capacity`]: 'Capacity',
  [`${PLANNING_BASE}/plan-of-record`]: 'Plan of Record',
  [`${PLANNING_BASE}/governance`]: 'Governance',
  [`${PLANNING_BASE}/assumptions-risks`]: 'Assumptions & Risks',
  [`${PLANNING_BASE}/changes`]: 'Changes',
  [`${PLANNING_BASE}/people-capacity`]: 'People & Capacity',
  [`${PLANNING_BASE}/areas`]: 'Areas',
  [`${PLANNING_BASE}/spreadsheet`]: 'Plan Workbench',
  [`${PLANNING_BASE}/launch-calendar`]: 'Launch Calendar',
  [`${PLANNING_BASE}/work-graph`]: 'Work Graph',
  [`${PLANNING_BASE}/milestones`]: 'Milestones',
  ...PLAN_SUB_TITLES,
};

export function resolvePlanningModuleTitle(pathname: string): string | null {
  if (pathname.startsWith(ENGINE_BASE)) return null;

  const cycleMatch = pathname.match(PLAN_CYCLE_RE);
  if (cycleMatch) {
    const [, , sub] = cycleMatch;
    if (!sub) return 'Plan';
    return PLAN_SUB_TITLES[sub] ?? 'Plan';
  }

  const exact = ROUTE_TITLES[pathname];
  if (exact) return exact;

  if (pathname.startsWith(`${PLANNING_BASE}/plan-of-record/`)) {
    return 'Plan of Record';
  }

  return null;
}

export function planningMobileBackHref(pathname: string): string | null {
  if (pathname.startsWith(`${ENGINE_BASE}/`)) return ENGINE_BASE;

  const cycleId = planCycleIdFromPath(pathname);
  const sub = planSubRouteFromPath(pathname);
  if (cycleId && sub) return planBaseHref(cycleId);
  if (cycleId && !sub) return PLANS_BASE;

  return null;
}

export function isPlanningTopLevelRoute(pathname: string): boolean {
  const topLevel = new Set([
    PLANNING_BASE,
    `${PLANNING_BASE}/dashboard`,
    ENGINE_BASE,
    `${PLANNING_BASE}/work-graph`,
    `${PLANNING_BASE}/capacity`,
    `${PLANNING_BASE}/milestones`,
    `${PLANNING_BASE}/plan-of-record`,
    PLANS_BASE,
  ]);
  return topLevel.has(pathname);
}
